import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { WaitingSpinner, GameEndedScreen, LoadingQuestion, TimerBar } from '../shared';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import type { GameStatistics } from '../../types/game';
import { HouseRunTaskCard, HouseRunPhotoSubmit, HouseRunVotePanel } from './houseRun';
import type { HouseRunTask } from './houseRun/HouseRunTaskCard';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/player-screen.css';
import { useTranslation } from '../../i18n';

interface HouseRunQuestion {
  questionId: string;
  timeLimit: number;
  startTime: number;
  playerId: string;
  teamId: 'A' | 'B';
  task?: HouseRunTask;
  submitted?: boolean;
  teamResolved?: boolean;
  accepted?: boolean;
  opposingSubmitted?: boolean;
  opposingResolved?: boolean;
  opposingAccepted?: boolean;
  judgePhoto?: string;
  judgeSubmitterName?: string;
  acceptVotes?: number;
  rejectVotes?: number;
  hasVoted?: boolean;
  questionNumber?: number;
  totalQuestions?: number;
}

interface HouseRunPlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; teamId?: 'A' | 'B' }>;
    teams?: { A: { score: number; name: string }; B: { score: number; name: string } };
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: {
      questionId?: string;
      id?: string;
      timeLimit?: number;
      startTime?: number;
      questionNumber?: number;
      totalQuestions?: number;
      isHouseRun?: boolean;
    };
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

const HouseRunPlayerDisplay: React.FC<HouseRunPlayerDisplayProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  onLoadingTimeout,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const [hrQuestion, setHrQuestion] = useState<HouseRunQuestion | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [myScore, setMyScore] = useState(0);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [judgePhoto, setJudgePhoto] = useState<string | undefined>();
  const [acceptVotes, setAcceptVotes] = useState(0);
  const [rejectVotes, setRejectVotes] = useState(0);
  const [hasVoted, setHasVoted] = useState(false);
  const currentQuestionIdRef = useRef<string | undefined>(undefined);
  const myTeamIdRef = useRef<'A' | 'B' | undefined>(undefined);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const me = roomState.players.find((p) => p.id === myPlayerId);
      if (me) {
        setMyScore(me.score);
        if (me.teamId) myTeamIdRef.current = me.teamId;
      }
    }
  }, [roomState.players, myPlayerId]);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setHrQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      setGameEnded(false);
      setHrQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      currentQuestionIdRef.current = undefined;
      setJudgePhoto(undefined);
      setHasVoted(false);
      setAcceptVotes(0);
      setRejectVotes(0);
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    if (!hrQuestion && roomState.currentQuestion?.isHouseRun) {
      const q = roomState.currentQuestion;
      const questionId = q.questionId || q.id;
      if (questionId && socket && roomState.roomName) {
        socket.emit('get-house-run-state', { roomName: roomState.roomName, questionId });
      }
    }
  }, [roomState.currentQuestion, hrQuestion, socket, roomState.roomName]);

  useEffect(() => {
    const handleQuestion = (data: HouseRunQuestion) => {
      const actualPlayerId = myPlayerId || socket.id;
      if (data.playerId && data.playerId !== actualPlayerId && data.playerId !== socket.id) return;

      currentQuestionIdRef.current = data.questionId;
      myTeamIdRef.current = data.teamId;
      setHrQuestion(data);
      setTimeRemaining(data.timeLimit);
      setUploading(false);
      setJudgePhoto(data.judgePhoto);
      setAcceptVotes(data.acceptVotes ?? 0);
      setRejectVotes(data.rejectVotes ?? 0);
      setHasVoted(!!data.hasVoted);
    };

    const handleSubmitted = (data: { questionId: string }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setHrQuestion((prev) => (prev ? { ...prev, submitted: true } : prev));
      setUploading(false);
    };

    const handleJudge = (data: {
      questionId: string;
      photoData: string;
      submitterName?: string;
      acceptVotes?: number;
      rejectVotes?: number;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setJudgePhoto(data.photoData);
      setAcceptVotes(data.acceptVotes ?? 0);
      setRejectVotes(data.rejectVotes ?? 0);
      setHasVoted(false);
      setHrQuestion((prev) =>
        prev ? { ...prev, opposingSubmitted: true, judgeSubmitterName: data.submitterName } : prev
      );
    };

    const handleVoteUpdate = (data: {
      questionId: string;
      acceptVotes: number;
      rejectVotes: number;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setAcceptVotes(data.acceptVotes);
      setRejectVotes(data.rejectVotes);
    };

    const handleTeamResolved = (data: {
      questionId: string;
      teamId: 'A' | 'B';
      accepted: boolean;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setHrQuestion((prev) => {
        if (!prev) return prev;
        if (data.teamId === prev.teamId) {
          return { ...prev, teamResolved: true, accepted: data.accepted };
        }
        return { ...prev, opposingResolved: true, opposingAccepted: data.accepted };
      });
      if (data.teamId !== hrQuestion?.teamId) {
        setJudgePhoto(undefined);
      }
    };

    const handleError = (data: { message: string }) => {
      setUploading(false);
      console.warn('House Run error:', data.message);
    };

    socket.on('house-run-question', handleQuestion);
    socket.on('house-run-submitted', handleSubmitted);
    socket.on('house-run-judge', handleJudge);
    socket.on('house-run-vote-update', handleVoteUpdate);
    socket.on('house-run-team-resolved', handleTeamResolved);
    socket.on('house-run-error', handleError);

    return () => {
      socket.off('house-run-question', handleQuestion);
      socket.off('house-run-submitted', handleSubmitted);
      socket.off('house-run-judge', handleJudge);
      socket.off('house-run-vote-update', handleVoteUpdate);
      socket.off('house-run-team-resolved', handleTeamResolved);
      socket.off('house-run-error', handleError);
    };
  }, [socket, myPlayerId, hrQuestion?.teamId]);

  useEffect(() => {
    if (!hrQuestion || hrQuestion.teamResolved) return;
    const interval = setInterval(() => {
      const remaining = hrQuestion.timeLimit - (Date.now() - hrQuestion.startTime);
      setTimeRemaining(Math.max(0, remaining));
    }, 100);
    return () => clearInterval(interval);
  }, [hrQuestion]);

  const handlePhotoSubmit = (photoData: string) => {
    if (!hrQuestion || hrQuestion.submitted || uploading) return;
    setUploading(true);
    socket.emit('house-run-submit', {
      roomName: roomState.roomName,
      questionId: hrQuestion.questionId,
      photoData,
    });
  };

  const handleVote = (vote: 'accept' | 'reject') => {
    if (!hrQuestion || hasVoted || !judgePhoto) return;
    const opposingTeam = hrQuestion.teamId === 'A' ? 'B' : 'A';
    setHasVoted(true);
    socket.emit('house-run-vote', {
      roomName: roomState.roomName,
      questionId: hrQuestion.questionId,
      teamId: opposingTeam,
      vote,
    });
  };

  if (gameEnded) {
    const handlePlaySameGame = () => {
      setGameEnded(false);
      setHrQuestion(null);
      setFinalLeaderboard([]);
      socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
    };

    const teamLeaderboard = roomState.teams
      ? [
          { playerId: 'A', playerName: roomState.teams.A.name, score: roomState.teams.A.score },
          { playerId: 'B', playerName: roomState.teams.B.name, score: roomState.teams.B.score },
        ].sort((a, b) => b.score - a.score)
      : finalLeaderboard.length > 0
        ? finalLeaderboard
        : (gameEndData?.finalLeaderboard ?? []);

    return (
      <GameEndedScreen
        gameIcon="🏠"
        finalLeaderboard={teamLeaderboard}
        myPlayerId={myPlayerId}
        myTeamId={myTeamIdRef.current ?? roomState.players.find((p) => p.id === myPlayerId)?.teamId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        statistics={gameStatistics ?? gameEndData?.gameStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  if (!hrQuestion) {
    if (roomState.currentQuestion?.isHouseRun) {
      return (
        <LoadingQuestion
          socket={socket}
          roomName={roomState.roomName}
          title={t('gameplay.loading.question')}
          onGoHome={onGoHome}
          onLongWaitTimeout={onLoadingTimeout}
        />
      );
    }
    return (
      <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
        <Header
          leftButton={onGoHome ? { icon: HeaderIcons.home, onClick: onGoHome, tooltip: t('common.home') } : undefined}
          roomCode={roomState.roomName}
        />
        <div className="game-player-wrapper">
          <WaitingSpinner message={t('gameplay.houseRun.waiting')} isLoading />
        </div>
      </div>
    );
  }

  const teamScore = roomState.teams?.[hrQuestion.teamId]?.score ?? 0;
  const canHunt = !hrQuestion.submitted && !hrQuestion.teamResolved;
  const showJudge = !!judgePhoto && !hrQuestion.opposingResolved;

  return (
    <div className="app" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E1E2E 100%)' }}>
      <Header
        leftButton={onGoHome ? { icon: HeaderIcons.home, onClick: onGoHome, tooltip: t('common.home') } : undefined}
        roomCode={roomState.roomName}
      />
      <div className="player-display" style={{ padding: '1rem', paddingTop: '1.5rem', width: '100%' }}>
        <div style={{ maxWidth: '480px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ textAlign: 'center' }}>
            <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
              {t('gameplay.houseRun.round', {
                current: hrQuestion.questionNumber ?? 1,
                total: hrQuestion.totalQuestions ?? 1,
              })}
            </span>
          </div>

          <TimerBar timeRemaining={timeRemaining} timeLimit={hrQuestion.timeLimit} />

          <div style={{ textAlign: 'center', fontSize: '0.9rem', opacity: 0.85 }}>
            {t('gameplay.houseRun.teamScore', { team: hrQuestion.teamId, score: teamScore })}
          </div>

          {hrQuestion.task && <HouseRunTaskCard task={hrQuestion.task} teamId={hrQuestion.teamId} />}

          {canHunt && (
            <HouseRunPhotoSubmit
              disabled={timeRemaining <= 0}
              uploading={uploading}
              onSubmit={handlePhotoSubmit}
            />
          )}

          {hrQuestion.submitted && !hrQuestion.teamResolved && (
            <p style={{ textAlign: 'center', margin: 0, opacity: 0.8, fontSize: '0.95rem' }}>
              {t('gameplay.houseRun.waitingForVote')}
            </p>
          )}

          {hrQuestion.teamResolved && (
            <p
              style={{
                textAlign: 'center',
                margin: 0,
                fontSize: '1rem',
                fontWeight: 700,
                color: hrQuestion.accepted ? '#22C55E' : '#F87171',
              }}
            >
              {hrQuestion.accepted
                ? t('gameplay.houseRun.accepted')
                : t('gameplay.houseRun.rejected')}
            </p>
          )}

          {showJudge && (
            <HouseRunVotePanel
              photoData={judgePhoto}
              submitterName={hrQuestion.judgeSubmitterName}
              acceptVotes={acceptVotes}
              rejectVotes={rejectVotes}
              hasVoted={hasVoted}
              disabled={timeRemaining <= 0}
              onVote={handleVote}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HouseRunPlayerDisplay;
